// 권역(시도·시군구) 설정 — org 문서(orgs/{orgId})에서 로드하며, 기본값은 org#1(부산)
// 주소 파싱·권역 내외 판정이 모두 이 모듈을 기준으로 동작한다(전국 일반화의 핵심).

export interface RegionConfig {
  sido: string; // 예: "부산광역시"
  sigungu: string[]; // 담당 시군구 이름 목록 (긴 이름 우선 매칭에 사용)
  aliases?: Record<string, string>; // 표기 별칭 → 정식 명칭 (예: 진구 → 부산진구)
}

export const BUSAN_REGION: RegionConfig = {
  sido: "부산광역시",
  sigungu: [
    "중구", "서구", "동구", "영도구", "부산진구", "동래구",
    "남구", "북구", "해운대구", "사하구", "금정구", "강서구",
    "연제구", "수영구", "사상구", "기장군",
  ],
  aliases: { 진구: "부산진구" },
};

let current: RegionConfig = BUSAN_REGION;

/** org 로드 시(store/org.ts) 호출 — org에 권역 설정이 없으면 부산 기본값 유지 */
export function setRegionConfig(cfg: Partial<RegionConfig> | null | undefined) {
  if (!cfg || !cfg.sido) { current = BUSAN_REGION; return; }
  // 부산 org가 시군구 목록을 따로 넣지 않았으면 검증된 부산 기본 목록 사용
  if (cfg.sido === BUSAN_REGION.sido && !cfg.sigungu?.length) { current = BUSAN_REGION; return; }
  current = {
    sido: cfg.sido,
    sigungu: cfg.sigungu?.length ? cfg.sigungu : [],
    aliases: cfg.aliases || {},
  };
}

export function getRegionConfig(): RegionConfig {
  return current;
}

/** 시도의 짧은 표기 — "부산광역시"→"부산", "경기도"→"경기", "전북특별자치도"→"전북" */
export function sidoShort(sido: string = current.sido): string {
  return sido.replace(/(특별자치시|특별자치도|특별시|광역시|도|시)$/, "");
}

/**
 * 주소에서 시군구 추출.
 * 1순위: 권역 시군구 목록 긴 이름 우선 매칭(별칭 포함) — 공백 없는 주소에도 동작.
 * 2순위(목록 미매칭): 일반 패턴 — 시도 토큰을 제거한 뒤 구/군, 없으면 일반 시.
 */
export function parseGuFrom(addr: string, cfg: RegionConfig = current): string {
  const names = [...cfg.sigungu, ...Object.keys(cfg.aliases || {})].sort((a, b) => b.length - a.length);
  for (const g of names) {
    if (g && addr.includes(g)) return cfg.aliases?.[g] || g;
  }
  // 일반 폴백: 17개 시·도의 정식·약칭만 지운다 — 임의의 "~도"를 지우면 "청도군"의 "청도"가 도 이름으로 사라진다(예행연습 04-8)
  const stripped = stripSido(addr);
  const m = stripped.match(/([가-힣]{1,8}(?:구|군))(?=[\s\d(,]|$)/);
  if (m) return normalizeGu(m[1]);
  const mSi = stripped.match(/([가-힣]{1,8}시)(?=[\s\d(,]|$)/);
  if (mSi) return mSi[1];
  // 세종처럼 시·군·구가 없는 시·도는 시·도 짧은 이름을 구 칸에 넣는다 — 동별 맡기·상차표가 "미상"으로 비지 않게(04-10)
  if (/(^|\s)세종/.test(addr)) return "세종";
  return "";
}

/** 17개 시·도의 정식·약칭(낱말 첫머리, 뒤에 공백·끝) — 주소에서 시·도만 정확히 지우는 데 쓴다 */
// 정식 표기(…광역시·…도)는 붙여 써도 지우고, 약칭(부산·경북)은 낱말로 떨어져 있을 때만 지운다 — "세종로"의 세종을 지우지 않게
const SIDO_FULL_RE = new RegExp(`(^|\\s)${'(?:서울특별시|부산광역시|대구광역시|인천광역시|광주광역시|대전광역시|울산광역시|세종특별자치시|경기도|강원특별자치도|강원도|충청북도|충청남도|전북특별자치도|전라북도|전라남도|경상북도|경상남도|제주특별자치도|제주도)'}`, "g");
const SIDO_SHORT_RE = new RegExp(`(^|\\s)${'(?:서울시|서울|부산시|부산|대구시|대구|인천시|인천|광주|대전시|대전|울산시|울산|세종시|세종|경기|강원|충북|충남|전북|전남|경북|경남|제주)'}(?=\\s|$)`, "g");
const stripSido = (addr: string) => addr.replace(SIDO_FULL_RE, " ").replace(SIDO_SHORT_RE, " ");

/**
 * 시·군·구 표기 통일 — 지도 서비스는 시 아래 구를 "성남시 분당구"로 돌려주고 주소 파서는 "분당구"를 넣는다.
 * 담당 매칭은 글자가 같아야 하므로 저장 전에 마지막 낱말("분당구")로 맞춘다(예행연습 04-9).
 */
export function normalizeGu(gu: string | null | undefined): string {
  const t = (gu || "").trim();
  if (!t.includes(" ")) return t;
  const parts = t.split(/\s+/);
  const last = parts[parts.length - 1];
  return /(구|군)$/.test(last) ? last : t;
}

/** 17개 시도의 짧은 표기 — 주소에 다른 시도 이름이 있는지 가리는 데 쓴다 */
export const SIDO_SHORTS = ["서울", "부산", "대구", "인천", "광주", "대전", "울산", "세종", "경기", "강원", "충북", "충남", "전북", "전남", "경북", "경남", "제주", "충청북", "충청남", "전라북", "전라남", "경상북", "경상남"];

/**
 * 주소가 권역 내인지.
 * - 우리 시도 이름이 있으면 안.
 * - 다른 시도 이름이 있으면 밖(예: 대구 업체의 "경북 경산시 …").
 * - 시도 이름이 없으면: 담당 시군구 목록이 있으면 그 안인지로, 목록이 없으면 안으로 본다.
 * (예전에는 "시군구가 하나라도 잡히면 안"이라 사실상 늘 안이었다 — 2026-09-27 타지역 점검)
 */
// 같은 시도의 두 표기 — 주소에는 "경북"도 "경상북도"도 온다
const SIDO_PAIRS: string[][] = [["충북", "충청북"], ["충남", "충청남"], ["전북", "전라북"], ["전남", "전라남"], ["경북", "경상북"], ["경남", "경상남"]];

export function isInRegion(addr: string, gu: string, cfg: RegionConfig = current): boolean {
  const own = sidoShort(cfg.sido);
  const ownNames = SIDO_PAIRS.find((p) => p.includes(own)) || [own];
  // 낱말 첫머리에서만 본다 — "해운대구"에도 "대구" 글자가 들어 있다
  if (ownNames.some((n) => new RegExp(`(^|\\s)${n}`).test(addr))) return true;
  // 다른 시도 이름으로 시작하는 낱말(뒤에 광역시·도·시·띄어쓰기)이 있으면 밖 — "부산시 …"도 밖.
  // 경기 업체의 "광주시"(경기 광주)만 광주광역시로 보지 않는다
  const other = SIDO_SHORTS.filter((s) => !ownNames.includes(s));
  const isOther = (s: string) => {
    if (own === "경기" && s === "광주" && /(^|\s)광주시/.test(addr)) return false;
    return new RegExp(`(^|\\s)${s}(?=광역시|특별시|특별자치시|특별자치도|도|시|\\s|$)`).test(addr);
  };
  if (other.some(isOther)) return false;
  if (!cfg.sigungu.length) return true;
  const known = [...cfg.sigungu, ...Object.values(cfg.aliases || {})];
  return !!gu && known.includes(gu);
}
